import { router, usePage } from "@inertiajs/vue3";

/**
 * A very simple pluralizer.
 * @param {string} word The word to pluralize.
 * @param {number} count The count to determine if pluralization is needed.
 * @returns {string}
 */
export function pluralize(word, count) {
    return count === 1 ? word : `${word}s`;
}

/**
 * Formats a number as a currency string.
 * @param {number} value The numeric value.
 * @param {string} currencyCode The ISO currency code (e.g., 'USD').
 * @returns {string}
 */
export function formatCurrency(value, currencyCode = "USD") {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue) || !currencyCode) {
        return value;
    }

    // Using Intl.NumberFormat for robust currency formatting
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
    }).format(numericValue);
}

export function config(key, $default = null) {
    return usePage().props.config[key] ?? $default;
}

export function reloadInertiaProps(keys) {
    router.reload({ only: keys });
}

/**
 * Help docs search (PRD: title + keywords only).
 * @param {Array} docs
 * @param {string} query
 * @returns {Array}
 */
export function filterHelpDocs(docs, query) {
    const term = (query || "").toString().toLowerCase().trim();
    if (!term) return Array.isArray(docs) ? docs : [];

    const list = Array.isArray(docs) ? docs : [];

    return list.filter((doc) => {
        const title = (doc?.title || "").toString();
        const keywords = Array.isArray(doc?.keywords) ? doc.keywords : [];
        const haystack = [title, ...keywords].join(" ").toLowerCase();

        return haystack.includes(term);
    });
}

/**
 * Build left-nav sections/categories for the Help Center.
 * @param {Array} docs
 * @returns {Array<{title: string, docs: Array, categories: Array<{title: string, docs: Array}>}>}
 */
export function buildHelpNavSections(docs) {
    const list = Array.isArray(docs) ? docs : [];
    const sectionMap = new Map();

    for (const doc of list) {
        const sectionTitle = doc?.section || "Docs";
        const categoryTitle = doc?.category || null;

        if (!sectionMap.has(sectionTitle)) {
            sectionMap.set(sectionTitle, {
                title: sectionTitle,
                docs: [],
                categories: new Map(),
            });
        }

        const section = sectionMap.get(sectionTitle);

        if (categoryTitle) {
            if (!section.categories.has(categoryTitle)) {
                section.categories.set(categoryTitle, {
                    title: categoryTitle,
                    docs: [],
                });
            }
            section.categories.get(categoryTitle).docs.push(doc);
        } else {
            section.docs.push(doc);
        }
    }

    return Array.from(sectionMap.values()).map((section) => ({
        title: section.title,
        docs: section.docs,
        categories: Array.from(section.categories.values()),
    }));
}
